'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import {
  markAllNotificationsReadAction,
  markNotificationReadAction,
} from '../actions/notification.actions'
import { notificationsQueryKey } from './use-notifications'
import { unreadNotificationCountQueryKey } from './use-unread-notification-count'
import type {
  AppNotification,
  AppNotificationPage,
  NotificationUnreadCount,
} from '../types/notification.types'

/**
 * What `useNotifications` actually caches: pages are `AppNotificationPage`
 * objects (`{ items, nextSkip, hasMore }`), not bare arrays. This used to say
 * `InfiniteData<AppNotification[]>`, which stopped being true when PB-531 moved
 * paging onto a server-issued cursor — every helper below then blew up on
 * `page.map`, and because the throw happened in `onMutate` react-query never
 * reached the mutationFn, so mark-read silently never left the browser
 * (PB-545).
 */
export type NotificationPages = InfiniteData<AppNotificationPage>

/**
 * Map every notification across all pages, preserving page boundaries/order.
 * Used by every cache-reconciling mutation so in-place edits never duplicate
 * or reorder loaded list entries. The page's `nextSkip`/`hasMore` cursor is
 * carried through untouched so infinite scroll survives a mark-read.
 */
export function mapPages(
  data: NotificationPages | undefined,
  fn: (n: AppNotification) => AppNotification
): NotificationPages | undefined {
  if (!data) return data
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: page.items.map(fn) })),
  }
}

/**
 * Apply an optimistic decrement to the cached unread totals, keeping the
 * personalized subset in step with the app-scoped total.
 *
 * `personalizedUnreadCount` is left `undefined` when it already was — a backend
 * that predates the field must not be papered over with a number we invented.
 *
 * The subtotal is clamped to the new total, for the same reason
 * `notificationApi.getUnreadCount` clamps the server's pair: the two decrements
 * come from different sources. `total` is however many ids the server cleared,
 * while `personalized` can only be counted off the pages this client has
 * LOADED — and on any page but /alerts it has loaded none. Without the clamp a
 * clear of unloaded personalized rows drops "All" without touching "For you",
 * rendering a "For you" badge above an "All" pill with no badge at all.
 */
function decrementUnreadCounts(
  prev: NotificationUnreadCount | undefined,
  by: { total: number; personalized: number }
): NotificationUnreadCount | undefined {
  if (!prev) return prev
  const unreadCount = Math.max(0, prev.unreadCount - by.total)
  return {
    unreadCount,
    personalizedUnreadCount:
      prev.personalizedUnreadCount === undefined
        ? undefined
        : Math.min(
            Math.max(0, prev.personalizedUnreadCount - by.personalized),
            unreadCount
          ),
  }
}

/**
 * Reconcile a set of server-cleared notification ids into local cache: flip
 * each matching entry to `read=true` **in place** (no dup / no reorder) and
 * drop the unread counts by however many were newly cleared.
 *
 * This is the shared "the server told us these are read now" step. Both the
 * view-to-clear batch flush (the read-receipt service) and any direct
 * entity-keyed clear use it, so there's a single reconciliation path. Unlike
 * the optimistic by-id mutations above, the caller doesn't know which ids will
 * clear until the server responds — so this runs on the returned id array.
 *
 * The personalized share of a clear can only be read off the loaded pages
 * (the server returns bare ids), so this decrement is a best-effort local
 * estimate — hence the invalidation at the end, which lets the authoritative
 * totals overwrite it rather than waiting out the 30s poll.
 */
export function reconcileClearedIds(
  qc: QueryClient,
  clearedIds: string[]
): void {
  if (clearedIds.length === 0) return
  const cleared = new Set(clearedIds)
  const loaded = qc.getQueryData<NotificationPages>(notificationsQueryKey)
  const clearedPersonalized =
    loaded?.pages
      .flatMap((p) => p.items)
      .filter((n) => cleared.has(n.id) && !n.read && n.personalized).length ?? 0
  qc.setQueryData<NotificationPages | undefined>(notificationsQueryKey, (prev) =>
    mapPages(prev, (n) =>
      cleared.has(n.id) && !n.read ? { ...n, read: true } : n
    )
  )
  qc.setQueryData<NotificationUnreadCount | undefined>(
    unreadNotificationCountQueryKey,
    (prev) =>
      decrementUnreadCounts(prev, {
        total: cleared.size,
        personalized: clearedPersonalized,
      })
  )
  qc.invalidateQueries({ queryKey: unreadNotificationCountQueryKey })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation<
    true,
    Error,
    string,
    { previous?: NotificationPages; previousCount?: NotificationUnreadCount }
  >({
    mutationFn: async (id) => {
      const res = await markNotificationReadAction(id)
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationsQueryKey })
      const previous = qc.getQueryData<NotificationPages>(notificationsQueryKey)
      const previousCount = qc.getQueryData<NotificationUnreadCount>(
        unreadNotificationCountQueryKey
      )
      const wasUnread = previous?.pages
        .flatMap((p) => p.items)
        .find((n) => n.id === id && !n.read)
      qc.setQueryData<NotificationPages | undefined>(
        notificationsQueryKey,
        (prev) => mapPages(prev, (n) => (n.id === id ? { ...n, read: true } : n))
      )
      if (wasUnread) {
        // A personalized notification counts against both pills, a
        // non-personalized one only against "All".
        qc.setQueryData<NotificationUnreadCount | undefined>(
          unreadNotificationCountQueryKey,
          decrementUnreadCounts(previousCount, {
            total: 1,
            personalized: wasUnread.personalized ? 1 : 0,
          })
        )
      }
      return { previous, previousCount }
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return
      if (ctx.previous) qc.setQueryData(notificationsQueryKey, ctx.previous)
      if (ctx.previousCount) {
        qc.setQueryData(unreadNotificationCountQueryKey, ctx.previousCount)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKey })
      qc.invalidateQueries({ queryKey: unreadNotificationCountQueryKey })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation<
    true,
    Error,
    void,
    { previous?: NotificationPages; previousCount?: NotificationUnreadCount }
  >({
    mutationFn: async () => {
      const res = await markAllNotificationsReadAction()
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationsQueryKey })
      const previous = qc.getQueryData<NotificationPages>(notificationsQueryKey)
      const previousCount = qc.getQueryData<NotificationUnreadCount>(
        unreadNotificationCountQueryKey
      )
      qc.setQueryData<NotificationPages | undefined>(
        notificationsQueryKey,
        (prev) => mapPages(prev, (n) => ({ ...n, read: true }))
      )
      // Nothing unread left anywhere, so both pills go to zero regardless of
      // what the personalized split was.
      qc.setQueryData<NotificationUnreadCount>(unreadNotificationCountQueryKey, {
        unreadCount: 0,
        personalizedUnreadCount: 0,
      })
      return { previous, previousCount }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return
      if (ctx.previous) qc.setQueryData(notificationsQueryKey, ctx.previous)
      if (ctx.previousCount) {
        qc.setQueryData(unreadNotificationCountQueryKey, ctx.previousCount)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationsQueryKey })
      qc.invalidateQueries({ queryKey: unreadNotificationCountQueryKey })
    },
  })
}
